import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { Camera, Loader2, Trash2, Images } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';

const resolveUrl = (url) =>
  url?.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;

// Attendee photo wall — opens once the event has started.
export const EventPhotoWall = ({ eventId }) => {
  const { user } = useAuth();
  const [wall, setWall] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/events/${eventId}/photo-wall`);
      setWall(res.data);
    } catch { /* silent */ }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  // Scroll to the wall when linked from the follow-up email (#photo-wall)
  useEffect(() => {
    if (wall?.event_started && window.location.hash === '#photo-wall') {
      setTimeout(() => document.getElementById('photo-wall')?.scrollIntoView({ behavior: 'smooth' }), 300);
    }
  }, [wall?.event_started]);

  if (!wall || !wall.event_started) return null;

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, 3);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} is over 5MB`); continue; }
      try {
        const fd = new FormData();
        fd.append('file', file);
        const up = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        await api.post(`/events/${eventId}/photo-wall`, { url: up.data.url });
      } catch (e) {
        toast.error(e.response?.data?.detail || `Could not share ${file.name}`);
      }
    }
    setUploading(false);
    await load();
    toast.success('Photos shared with the group!');
  };

  const handleDelete = async (photoId) => {
    try {
      await api.delete(`/events/${eventId}/photo-wall/${photoId}`);
      setWall((w) => ({ ...w, photos: w.photos.filter((p) => p.id !== photoId) }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove photo');
    }
  };

  return (
    <div id="photo-wall" className="mt-8 pt-6 border-t border-border-subtle" data-testid="photo-wall-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-display text-xl text-navy flex items-center gap-2">
          <Images size={20} className="text-teal" /> Photo Wall
        </h3>
        {wall.can_upload && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
              data-testid="photo-wall-file-input"
            />
            <Button
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="rounded-full bg-teal hover:bg-teal-deep text-white"
              data-testid="photo-wall-upload-btn"
            >
              {uploading ? <Loader2 size={15} className="animate-spin mr-2" /> : <Camera size={15} className="mr-2" />}
              Share photos
            </Button>
          </>
        )}
      </div>

      {wall.photos.length === 0 ? (
        <p className="text-sm text-text-mid bg-cream/60 rounded-xl p-4 text-center" data-testid="photo-wall-empty">
          {wall.can_upload
            ? 'No photos yet — be the first to share a moment from this event!'
            : 'Attendees haven\'t shared photos yet.'}
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="photo-wall-grid">
          {wall.photos.map((p) => (
            <div key={p.id} className="relative group rounded-xl overflow-hidden bg-cream aspect-square">
              <img src={resolveUrl(p.url)} alt={`Shared by ${p.uploader_name}`} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1.5">
                <p className="text-[11px] text-white truncate">{p.uploader_name}</p>
              </div>
              {(p.user_id === user?.id || user?.is_admin) && (
                <button
                  onClick={() => handleDelete(p.id)}
                  className="absolute top-2 right-2 bg-black/50 hover:bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`photo-wall-delete-${p.id}`}
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventPhotoWall;
